import { getLocale } from 'umi';

import type { TChartType } from '@dbl/core/chart';
import { datalabWithTokenHttp, type TDatalabHttpConfig } from '@dbl/services/common';

/** 图表计算请求统一超时（毫秒）。 */
export const CHART_CALCULATION_TIMEOUT_MS = 120000;

/** 图表计算接口配置（analysis / dashboard 共用）。 */
export interface IChartCalculationRequestConfig {
  url: string;
  extraData?: Record<string, unknown>;
}

export function buildChartCalculationPayload<TParam extends object>(
  param: TParam,
  extraData?: Record<string, unknown>,
): Record<string, unknown> {
  return {
    ...param,
    request_time: new Date().toISOString(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: getLocale(),
    ...extraData,
  };
}

/**
 * 根据配置创建图表计算 API 函数（analysis / dashboard 共用）。
 *
 * @template TResponse 下传 wire 类型；未指定时默认 `unknown`（报表等模块待补类型）。
 */
export type TChartCalculationHandler<
  TParam extends object,
  TResponse = unknown,
> = (param: TParam) => Promise<TResponse>;

type TChartResponseUnion<
  TResponseMap extends Partial<Record<TChartType, unknown>>,
> = TResponseMap[keyof TResponseMap & TChartType];

export function createChartCalculationHandler<
  TParam extends object,
  TResponse = unknown,
>(
  config: IChartCalculationRequestConfig,
): TChartCalculationHandler<TParam, TResponse> {
  const { url, extraData } = config;

  return (param: TParam) => {
    const payload = buildChartCalculationPayload(param, extraData);
    return datalabWithTokenHttp(
      {
        url,
        method: 'post',
        data: payload,
        timeout: CHART_CALCULATION_TIMEOUT_MS,
      } as TDatalabHttpConfig,
    ).then(data => {
      return data as TResponse;
    });
  };
}

/** 由 config 表构建 chartType → handler 映射，支持覆盖个别类型。 */
export function buildChartHandlerRegistry<
  TParam extends object,
  TResponse = unknown,
>(
  configByType: Partial<Record<TChartType, IChartCalculationRequestConfig>>,
): Partial<Record<TChartType, TChartCalculationHandler<TParam, TResponse>>> {
  const handlers: Partial<
    Record<TChartType, TChartCalculationHandler<TParam, TResponse>>
  > = {};

  (Object.keys(configByType) as TChartType[]).forEach((chartType: TChartType) => {
    const config = configByType[chartType];
    if (!config) {
      return;
    }

    handlers[chartType] = createChartCalculationHandler<TParam, TResponse>(
      config,
    );
  });

  return handlers;
}

/**
 * 按 chartType 分发图表计算请求，未命中时回退 fallback。
 *
 * @template TResponseMap 各 chartType 下传 wire 映射；省略时各类型均为 `unknown`（报表模块占位）。
 */
export function createChartCalculationDispatcher<
  TParam extends object,
  TResponseMap extends Partial<Record<TChartType, unknown>> = Partial<
    Record<TChartType, unknown>
  >,
>(options: {
  registry: Partial<
    Record<
      TChartType,
      TChartCalculationHandler<TParam, TChartResponseUnion<TResponseMap>>
    >
  >;
  fallback: TChartCalculationHandler<TParam, TChartResponseUnion<TResponseMap>>;
}): <K extends keyof TResponseMap & TChartType>(
  chartType: K,
  params: TParam,
) => Promise<TResponseMap[K]> {
  const { registry, fallback } = options;

  return ((chartType: TChartType, params: TParam) => {
    const handler = registry[chartType] ?? fallback;
    return handler(params) as Promise<TResponseMap[typeof chartType & keyof TResponseMap & TChartType]>;
  }) as <K extends keyof TResponseMap & TChartType>(
    chartType: K,
    params: TParam,
  ) => Promise<TResponseMap[K]>;
}
